(() => {
  if (customElements.get('faf-desktop-navigation')) return;
  const openRoots = new WeakMap();

  class FAFDesktopNavigation extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.desktop = matchMedia('(min-width: 1100px)');
      this.hover = matchMedia('(hover: hover)');
      this.buttons = [...this.querySelectorAll('.faf-desktop-nav__trigger[aria-controls]')];
      const panels = [...this.querySelectorAll('.faf-desktop-panel')];
      this.panels = this.buttons.map(button => panels.find(panel => panel.id === button.getAttribute('aria-controls')));
      this.activeIndex = -1;
      panels.forEach(panel => { panel.hidden = true; });
      this.buttons.forEach((button, index) => {
        button.setAttribute('aria-expanded', 'false');
        button.addEventListener('click', () => {
          this.clearTimers();
          if (this.activeIndex === index) this.close();
          else this.toggle(index);
        }, options);
        button.addEventListener('keydown', event => {
          if (event.key !== 'ArrowDown' || event.altKey || event.ctrlKey || event.metaKey) return;
          event.preventDefault();
          this.clearTimers();
          if (this.toggle(index)) this.firstDestination(this.panels[index])?.focus({ preventScroll: true });
        }, options);
        button.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'mouse' || !this.hover.matches || !this.desktop.matches) return;
          this.clearTimers();
          this.hoverTimer = setTimeout(() => {
            if (!this.isConnected || this.activePanel()?.contains(this.ownerDocument.activeElement)) return;
            this.toggle(index);
          }, 350);
        }, options);
        button.addEventListener('pointerleave', () => clearTimeout(this.hoverTimer), options);
      });
      this.addEventListener('click', event => {
        if (!(event.target instanceof Element)) return;
        const close = event.target.closest('[data-faf-close]');
        if (close && this.contains(close)) {
          event.preventDefault();
          this.close(true);
          return;
        }
        const choice = event.target.closest('button[data-faf-room-open]');
        if (choice && !choice.disabled && this.activePanel()?.contains(choice)) this.selectRoom(this.activePanel(), choice);
        const anchor = event.target.closest('a[href]');
        if (anchor && this.contains(anchor) && !event.defaultPrevented && event.button === 0 &&
          !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
          anchor.getAttribute('download') === null && ['', '_self'].includes((anchor.getAttribute('target') || '').toLowerCase())) this.close();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || event.defaultPrevented || this.activeIndex < 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
      }, options);
      this.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), options);
      this.addEventListener('pointerleave', () => {
        this.clearTimers();
        this.closeTimer = setTimeout(() => {
          if (!this.contains(this.ownerDocument.activeElement)) this.close();
        }, 300);
      }, options);
      this.addEventListener('focusout', () => {
        cancelAnimationFrame(this.focusFrame);
        this.focusFrame = requestAnimationFrame(() => {
          if (this.isConnected && !this.contains(this.ownerDocument.activeElement)) this.close();
        });
      }, options);
      this.ownerDocument.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.close();
      }, options);
      this.ownerDocument.addEventListener('focusin', event => {
        if (!this.contains(event.target)) this.close();
      }, options);
      this.ownerDocument.addEventListener('input', event => {
        if (event.target instanceof Element && event.target.matches('input[type="search"][name="q"]')) this.close();
      }, options);
      this.desktop.addEventListener('change', event => {
        const restoreMobile = !event.matches && this.contains(this.ownerDocument.activeElement);
        this.close();
        if (restoreMobile) this.headerRoot()?.querySelector('.faf-mobile-nav__trigger')?.focus({ preventScroll: true });
      }, options);
      const viewportOptions = { ...options, passive: true };
      window.addEventListener('resize', () => this.scheduleFit(), viewportOptions);
      window.addEventListener('scroll', () => this.scheduleFit(), viewportOptions);
      window.visualViewport?.addEventListener('resize', () => this.scheduleFit(), viewportOptions);
      window.visualViewport?.addEventListener('scroll', () => this.scheduleFit(), viewportOptions);
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
        this.resizeObserver.observe(this);
        const header = this.headerRoot();
        if (header && header !== this) this.resizeObserver.observe(header);
      }
    }

    disconnectedCallback() { this.cleanup(); }

    headerRoot() {
      return this.closest('.shopify-section-header') || this.closest('sticky-header') || this.closest('header');
    }

    activePanel() { return this.panels?.[this.activeIndex]; }

    firstDestination(panel) {
      return [...(panel?.querySelectorAll('a[href], button[data-faf-room-open]') || [])].find(element =>
        !element.closest('[hidden], [inert]') && !element.disabled && element.getClientRects().length);
    }

    selectRoom(panel, button) {
      const rooms = [...panel.querySelectorAll('[data-faf-room-panel]')];
      const selected = rooms.find(room => room.id === button.dataset.fafRoomOpen);
      if (!selected) return false;
      panel.querySelectorAll('button[data-faf-room-open]').forEach(choice => {
        choice.setAttribute('aria-expanded', String(choice.dataset.fafRoomOpen === selected.id));
      });
      rooms.forEach(room => { room.hidden = room !== selected; });
      selected.scrollTop = 0;
      this.scheduleFit();
      return true;
    }

    resetRooms(panel) {
      const buttons = [...panel.querySelectorAll('button[data-faf-room-open]')];
      for (const button of buttons) {
        if (!button.disabled && this.selectRoom(panel, button)) break;
      }
    }

    toggle(index) {
      if (!this.desktop?.matches || !this.panels?.[index]) return false;
      this.clearTimers();
      if (this.activeIndex === index) {
        this.scheduleFit();
        return true;
      }
      this.restorePanelSize();
      this.activeIndex = index;
      this.buttons.forEach((button, position) => button.setAttribute('aria-expanded', String(position === index)));
      this.panels.forEach((panel, position) => { if (panel) panel.hidden = position !== index; });
      const panel = this.activePanel();
      panel.scrollTop = 0;
      this.resetRooms(panel);
      this.sizeStyle = {
        panel,
        value: panel.style.getPropertyValue('--faf-panel-available-height'),
        priority: panel.style.getPropertyPriority('--faf-panel-available-height')
      };
      this.holdStickyHeader();
      this.syncOpenClass(true);
      this.fitPanel();
      this.scheduleFit();
      return true;
    }

    fitPanel() {
      const panel = this.activePanel();
      if (!panel || panel.hidden || !this.desktop?.matches) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const height = Math.max(0, Math.floor(bottom - panel.getBoundingClientRect().top - 16));
      panel.style.setProperty('--faf-panel-available-height', height + 'px');
    }

    scheduleFit() {
      if (this.activeIndex < 0 || this.fitFrame) return;
      this.fitFrame = requestAnimationFrame(() => {
        this.fitFrame = null;
        if (this.isConnected) this.fitPanel();
      });
    }

    restorePanelSize() {
      if (!this.sizeStyle) return;
      const { panel, value, priority } = this.sizeStyle;
      if (value) panel.style.setProperty('--faf-panel-available-height', value, priority);
      else panel.style.removeProperty('--faf-panel-available-height');
      this.sizeStyle = null;
    }

    holdStickyHeader() {
      if (this.stickyHold) return;
      const header = this.closest('sticky-header');
      if (!header) return;
      this.stickyHold = { header, owned: Object.prototype.hasOwnProperty.call(header, 'preventHide'), value: header.preventHide };
      header.preventHide = true;
    }

    releaseStickyHeader() {
      if (!this.stickyHold) return;
      const { header, owned, value } = this.stickyHold;
      if (header.preventHide === true) {
        if (owned) header.preventHide = value;
        else delete header.preventHide;
      }
      this.stickyHold = null;
    }

    syncOpenClass(open) {
      const root = open ? this.ownerDocument.documentElement : this.openRoot;
      if (!root) return;
      let owners = openRoots.get(root);
      if (!owners) {
        owners = new Set();
        openRoots.set(root, owners);
      }
      if (open) owners.add(this);
      else owners.delete(this);
      this.openRoot = open ? root : null;
      root.classList.toggle('faf-desktop-menu-open', owners.size > 0);
      if (!owners.size) openRoots.delete(root);
    }

    clearTimers() {
      clearTimeout(this.hoverTimer);
      clearTimeout(this.closeTimer);
    }

    close(restoreFocus = false) {
      this.clearTimers();
      const button = this.buttons?.[this.activeIndex];
      this.activeIndex = -1;
      this.buttons?.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      this.panels?.forEach(panel => { if (panel) panel.hidden = true; });
      this.restorePanelSize();
      this.releaseStickyHeader();
      this.syncOpenClass(false);
      cancelAnimationFrame(this.fitFrame);
      this.fitFrame = null;
      if (restoreFocus && this.isConnected) {
        const destination = this.desktop?.matches ? button : this.headerRoot()?.querySelector('.faf-mobile-nav__trigger');
        destination?.focus({ preventScroll: true });
      }
    }

    cleanup() {
      this.close();
      this.listeners?.abort();
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      cancelAnimationFrame(this.focusFrame);
    }
  }

  customElements.define('faf-desktop-navigation', FAFDesktopNavigation);
})();
